/**
 * Operator role definitions + their default permission grants.
 *
 * The six pre-set roles are:
 *
 *   - trader     read-all, propose assignment changes, NOT approve. Can place
 *                manual orders (with risk gating). Cannot grant overrides.
 *   - risk       read-all. Can pause assignments / rollouts / kill-switch.
 *                Cannot create assignments or grant mandate overrides.
 *   - ops        read-all. Approves four-eyes for assignment changes; resolves
 *                non-mandate remediation items.
 *   - compliance read-all. Edits eligibility rules. Grants mandate overrides
 *                (FOUR-EYES from compliance role on BOTH sides).
 *   - admin      everything. Used sparingly.
 *   - readonly   read-all, no mutations.
 *
 * Permissions are referenced as `"<resource>:<verb>"` strings — flat namespace,
 * easy to grep, and trivially serialisable for /admin/roles.
 *
 * The `permissions` map is the SOURCE OF TRUTH. `usePermission(action)` in
 * the auth hook reads from this; route middleware reads from this; server
 * action guards read from this. One place to change, audit-friendly.
 */

import type { OperatorRole } from '@/types/domain';

/* ============================================================================
 * Action vocabulary — closed set. New verbs require a code change.
 * ============================================================================ */

export const PERMISSIONS = [
  // Strategies
  'strategies:read',
  'strategies:start_stop',
  'strategies:reload',
  'strategies:edit_params',
  'strategies:arm_engine',

  // Brokers
  'brokers:read',
  'brokers:create',
  'brokers:rotate_credentials',

  // Subscriptions (execution-layer)
  'subscriptions:read',
  'subscriptions:create',
  'subscriptions:pause',
  'subscriptions:edit_sizing',

  // Assignments (control-plane)
  'assignments:read',
  'assignments:create',
  'assignments:propose',
  'assignments:approve',
  'assignments:pause',
  'assignments:resume',
  'assignments:terminate',
  'assignments:override_sizing',

  // Cohorts
  'cohorts:read',
  'cohorts:create',
  'cohorts:edit_rule',
  'cohorts:refresh',
  'cohorts:delete',

  // Rollouts
  'rollouts:read',
  'rollouts:create',
  'rollouts:promote',
  'rollouts:pause',
  'rollouts:abort',

  // Eligibility
  'eligibility:read',
  'eligibility:create',
  'eligibility:edit',
  'eligibility:delete',
  'eligibility:override_request',
  'eligibility:override_approve',

  // Templates
  'templates:read',
  'templates:create',
  'templates:edit',
  'templates:apply',

  // Mandate
  'mandate:read',
  'mandate:amend',
  'mandate:override_request',
  'mandate:override_approve', // Compliance-only on BOTH sides

  // Remediation
  'remediation:read',
  'remediation:claim',
  'remediation:resolve_non_mandate',
  'remediation:resolve_with_override', // Compliance-only

  // Clients
  'clients:read',
  'clients:edit_meta',
  'clients:edit_tier',
  'clients:offboard',

  // Risk
  'risk:read',
  'risk:create_rule',
  'risk:edit_rule',
  'risk:delete_rule',
  'risk:override_limit',
  'risk:kill_switch',

  // Orders / Trades
  'orders:read',
  'orders:place_manual',
  'orders:cancel',
  'trades:read',

  // Reconciliation
  'reconciliation:read',
  'reconciliation:resolve_break',

  // Audit / Compliance Pack
  'audit:read',
  'compliance_pack:export',

  // Admin
  'admin:read',
  'admin:invite_user',
  'admin:remove_user',
  'admin:change_role',
  'admin:edit_feature_flag',
  'admin:edit_webhook',

  // Settings (per-operator self-service)
  'settings:edit_self',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/* ============================================================================
 * Role → permissions matrix
 * ============================================================================ */

const ALL: Permission[] = [...PERMISSIONS];

const READ_ONLY: Permission[] = ALL.filter((p) => p.endsWith(':read'));

const TRADER: Permission[] = [
  ...READ_ONLY,
  // Trader can propose assignment changes (creates land in PENDING_APPROVAL).
  'assignments:propose',
  // Trader can edit sizing on draft assignments only — runtime guard in lifecycle.
  'assignments:override_sizing',
  'subscriptions:edit_sizing',
  'strategies:start_stop',
  'strategies:reload',
  'strategies:arm_engine',
  'orders:place_manual',
  'orders:cancel',
  // Trader cannot approve four-eyes; cannot grant overrides.
  'settings:edit_self',
];

// Manual-Trader: a FOCUSED discretionary role — NOT read-all. Sees the Trade
// section (Terminal/Positions/Trades/P&L) + market data (incl. Option Chains,
// which are ungated tabs) and can place/cancel manual orders. Everything else
// (strategies, risk, accounts, admin) stays hidden.
const MANUAL_TRADER: Permission[] = [
  'orders:read',
  'trades:read',
  'orders:place_manual',
  'orders:cancel',
  'settings:edit_self',
];

const RISK: Permission[] = [
  ...READ_ONLY,
  'assignments:pause',
  'rollouts:pause',
  'risk:override_limit',
  'risk:kill_switch',
  'risk:create_rule',
  'risk:edit_rule',
  'risk:delete_rule',
  'settings:edit_self',
];

const OPS: Permission[] = [
  ...READ_ONLY,
  // Ops is the second-eye for assignment / rollout / template / risk actions
  // (except mandate overrides). Permission to approve = ability to act as
  // four-eyes approver in the four-eyes engine.
  'assignments:approve',
  'rollouts:promote',
  'rollouts:abort',
  // Non-mandate remediation (unwind, reassign, mandate-amend trigger).
  'remediation:claim',
  'remediation:resolve_non_mandate',
  // Day-to-day cohort and template upkeep.
  'cohorts:create',
  'cohorts:edit_rule',
  'cohorts:refresh',
  'cohorts:delete',
  'templates:create',
  'templates:edit',
  'templates:apply',
  'reconciliation:resolve_break',
  'compliance_pack:export',
  'settings:edit_self',
];

const COMPLIANCE: Permission[] = [
  ...READ_ONLY,
  // Compliance owns eligibility rules and mandates.
  'eligibility:create',
  'eligibility:edit',
  'eligibility:delete',
  'eligibility:override_request',
  'eligibility:override_approve',
  'mandate:amend',
  'mandate:override_request',
  'mandate:override_approve', // The critical one. Four-eyes engine requires BOTH sides Compliance.
  'remediation:resolve_with_override',
  'compliance_pack:export',
  'audit:read',
  'settings:edit_self',
];

const ADMIN: Permission[] = ALL;

export const ROLE_PERMISSIONS: Record<OperatorRole, Permission[]> = {
  trader: TRADER,
  manual_trader: MANUAL_TRADER,
  risk: RISK,
  ops: OPS,
  compliance: COMPLIANCE,
  admin: ADMIN,
  readonly: READ_ONLY,
};

/** Pretty role label for the UI. Keep aligned with `OPERATOR_ROLES`. */
export const ROLE_LABEL: Record<OperatorRole, string> = {
  trader: 'Trader',
  manual_trader: 'Manual Trader',
  risk: 'Risk',
  ops: 'Operations',
  compliance: 'Compliance',
  admin: 'Administrator',
  readonly: 'Read-only',
};

/** Short description shown in the role picker. */
export const ROLE_DESCRIPTION: Record<OperatorRole, string> = {
  trader: 'Reads all, proposes assignments, runs engine; cannot approve four-eyes or grant overrides.',
  manual_trader: 'Discretionary trading — the Trade section + market data (option chains); places manual orders. No strategies, risk, accounts, or admin.',
  risk: 'Reads all, pauses assignments / rollouts, edits risk rules, fires kill-switch.',
  ops: 'Reads all, approves four-eyes for assignments, manages cohorts and templates, resolves recon.',
  compliance: 'Reads all, edits eligibility, amends mandates, grants overrides (with second-eye Compliance).',
  admin: 'Full access. Used sparingly.',
  readonly: 'View-only across the dashboard. No mutations.',
};
